import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectName, selectUserId, selectUsers } from '../login/loginSlice';
import { getPacks, createPackFiles, getPacksUnreadSummary, markPacksViewedUser, setBuhBillPaymentClient } from './packApi';
import FileMessage from './FileMessage';
import { newTask, fileSelect, closeIconTask } from '../../static/svgs';
import '../tasks/task.css';
import './documents.css';
import '../../static/css/main.css';
import { connectionFailedMessage } from '../../utils/fetchWithRetry';
import { formatMainBuhBillLine } from '../../utils/buhBillPayment';
import CompanyPickerList from '../shared/CompanyPickerList';
import { useAccountPicker } from '../../hooks/useAccountPicker';
import { useSectionMenuReset } from '../../context/SectionMenuResetContext';
import { usePickerBadgeMap, useAccountBadgesContext } from '../../context/AccountBadgesContext';
import { arrowLIcon } from '../../static/svgs';
import '../shared/companyPicker.css';

const PACK_SECTIONS = [
    { id: 'invoices', title: 'Счета-фактуры, УПД, акты, накладные, чеки', group: 'company' },
    { id: 'bank', title: 'Банковские выписки', group: 'company' },
    { id: 'tax', title: 'Налоги, декларации', group: 'accounting' },
    { id: 'salary', title: 'Заработная плата, взносы', group: 'accounting' },
    { id: 'buh_bill', title: 'Счета за бухгалтерские услуги', group: 'accounting' },
];

const MONTH_NAMES_RU = [
    'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
    'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];

const getCurrentPeriod = () => {
    const d = new Date();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${d.getFullYear()}-${month}`;
};

/** Выписку за месяц можно загружать только после окончания этого месяца (строго раньше текущего YYYY-MM). */
const isBankStatementPeriodClosed = (period) => Boolean(period && period < getCurrentPeriod());

const formatStatementPeriodLabel = (period) => {
    if (!period || !/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) return 'Выписки';
    const [yearStr, monthStr] = period.split('-');
    const monthName = MONTH_NAMES_RU[Number(monthStr) - 1] || monthStr;
    return `Выписки за ${monthName} ${yearStr}`;
};

const getBankStatementCards = (packs) => {
    const grouped = new Map();
    (packs || []).forEach((pack) => {
        const createdAt = new Date(pack.created_at);
        const fallbackPeriod = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
        const period = pack.statement_period || fallbackPeriod;
        if (!grouped.has(period)) {
            grouped.set(period, { period, files: [], packs: [] });
        }
        const card = grouped.get(period);
        if (Array.isArray(pack.files) && pack.files.length > 0) {
            card.files.push(...pack.files);
        }
        card.packs.push(pack);
    });
    return Array.from(grouped.values()).sort((a, b) => a.period.localeCompare(b.period));
};

/** Дата из имени файла на бэке: ...-timestamp.ext */
const parseTimestampFromFilePath = (path) => {
    if (!path) return null;
    const m = String(path).match(/-(\d{10,13})(?=\.[^./]+$)/);
    if (!m) return null;
    const n = Number(m[1]);
    const ms = n < 1e12 ? n * 1000 : n;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
};

const getFirstFileUploadDateForCard = (card) => {
    const fromPaths = (card.files || [])
        .map((f) => parseTimestampFromFilePath(f.path))
        .filter(Boolean);
    if (fromPaths.length > 0) {
        return new Date(Math.min(...fromPaths.map((d) => d.getTime())));
    }
    const packsWithFiles = (card.packs || []).filter(
        (p) => Array.isArray(p.files) && p.files.length > 0
    );
    if (packsWithFiles.length > 0) {
        const times = packsWithFiles.map((p) => new Date(p.created_at).getTime());
        return new Date(Math.min(...times));
    }
    return null;
};

const PACK_DATETIME_OPTS = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
};

/** Клиент: подпись «Дата отправки» и дата+время на карточке пака (все подразделы) */
function getClientPackDateLabelAndValue(item, isTaxOrSalary) {
    const fmt = (d) => new Date(d).toLocaleString('ru-RU', PACK_DATETIME_OPTS);

    if (!isTaxOrSalary) {
        return {
            label: 'Дата отправки',
            value: fmt(item.created_at),
        };
    }
    if (item.published_at) {
        return {
            label: 'Дата отправки',
            value: fmt(item.published_at),
        };
    }
    if (item.publish_after) {
        return {
            label: 'Дата отправки',
            value: fmt(item.publish_after),
        };
    }
    return {
        label: 'Дата отправки',
        value: fmt(item.created_at),
    };
}

const formatAmountRub = (amount) => {
    if (amount == null || amount === '') return null;
    const val = Number(amount);
    if (!Number.isFinite(val) || val <= 0) return null;
    return `${val.toLocaleString('ru-RU')} руб`;
};

const getBuhBillPackTitle = (item) => formatMainBuhBillLine(item);

const isAccountantPackSection = (sectionId) =>
    sectionId === 'tax' || sectionId === 'salary' || sectionId === 'buh_bill';

const isBuhBillPaidChecked = (status) =>
    status === 'client_paid' || status === 'confirmed';

const normalizeBuhPaymentStatus = (status) => {
    const s = String(status || 'created').trim().toLowerCase();
    if (s === 'client_paid' || s === 'confirmed') return s;
    return 'created';
};

const ChevronLeftIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M15 6L9 12L15 18" stroke="#3262FF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronRightIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Documents = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const username = useSelector(selectName);
    const user_id = useSelector(selectUserId);
    const users = useSelector(selectUsers);
    const { multiple, pickerOpen, selectAccount, openPicker, closePicker } = useAccountPicker(users);
    const pickerBadgeByUserId = usePickerBadgeMap('documents');
    const { refresh: refreshAccountBadges } = useAccountBadgesContext();
    const [packs, setPacks] = useState([]);
    const [selectedSection, setSelectedSection] = useState(null);

    useSectionMenuReset('/documents', () => {
        if (multiple && !pickerOpen) {
            setSelectedSection(null);
            openPicker();
            return true;
        }
        return false;
    });

    const [isModalVisible, setModalVisible] = useState(false);
    const [comment, setComment] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isOwner, setIsOwner] = useState(true);
    const [unreadByType, setUnreadByType] = useState({ invoices: 0, bank: 0, tax: 0, salary: 0, buh_bill: 0 });
    const [bankEmptyPeriodsCount, setBankEmptyPeriodsCount] = useState(0);
    const [skipBankBadge, setSkipBankBadge] = useState(false);
    const [uploadingBankPeriod, setUploadingBankPeriod] = useState(null);
    const [packFilesModalVisible, setPackFilesModalVisible] = useState(false);
    const [currentPackFiles, setCurrentPackFiles] = useState([]);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [docSearchQuery, setDocSearchQuery] = useState('');
    const [searchMatchIndices, setSearchMatchIndices] = useState([]);
    const [activeSearchMatchIdx, setActiveSearchMatchIdx] = useState(0);
    const [docContextMenu, setDocContextMenu] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('');
    const [packsLoading, setPacksLoading] = useState(false);
    const [buhPaymentUpdatingId, setBuhPaymentUpdatingId] = useState(null);
    const [buhPaymentConfirm, setBuhPaymentConfirm] = useState(null);
    const scrollContainerRef = useRef(null);
    const lastPackRef = useRef(null);
    const bankStatementFileInputRef = useRef(null);
    /** Период месяца для загрузки выписки — ref, т.к. setState до открытия диалога файла может не успеть */
    const bankUploadPeriodRef = useRef(null);
    /** Синхронная защита от двойной отправки; не использовать uploadingBankPeriod в guard — stale closure */
    const bankUploadBusyRef = useRef(false);

    useEffect(() => {
        if (user_id && selectedSection) {
            fetchPacks();
        }
    }, [user_id, selectedSection]);

    useEffect(() => {
        if (user_id) fetchUnreadSummary();
    }, [user_id]);

    useEffect(() => {
        const pickAccountFromState = () => {
            const targetId = location.state?.accountUserId;
            if (!targetId || !users?.length) {
                closePicker();
                return;
            }
            const user = users.find((u) => Number(u.user_id) === Number(targetId));
            if (user) {
                selectAccount(user);
            } else {
                closePicker();
            }
        };

        if (location.state?.openBank) {
            pickAccountFromState();
            setSelectedSection('bank');
            navigate(location.pathname, { replace: true, state: {} });
            return;
        }
        if (location.state?.openBuhBill) {
            pickAccountFromState();
            setSelectedSection('buh_bill');
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate, closePicker, selectAccount, users]);

    useEffect(() => {
        if (user_id && !selectedSection) {
            refreshBankEmptyPeriodsCount();
        }
    }, [user_id, selectedSection]);

    // Автопрокрутка списка пакетов вниз при входе в подраздел (выписки — без автопрокрутки)
    useEffect(() => {
        if (
            packs.length > 0 &&
            scrollContainerRef.current &&
            selectedSection &&
            selectedSection !== 'bank'
        ) {
            setTimeout(() => {
                const container = scrollContainerRef.current;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            }, 100);
        }
    }, [packs, selectedSection]);

    const refreshBankEmptyPeriodsCount = () => {
        if (!user_id) return;
        getPacks(user_id, 'bank')
            .then((data) => {
                const cards = getBankStatementCards(data || []);
                const n = cards.filter(
                    (c) =>
                        isBankStatementPeriodClosed(c.period) && (c.files || []).length === 0
                ).length;
                setBankEmptyPeriodsCount(n);
            })
            .catch((e) => console.warn('refreshBankEmptyPeriodsCount', e));
    };

    const fetchPacks = () => {
        if (!selectedSection) return;
        setPacksLoading(true);
        setConnectionStatus('');
        getPacks(user_id, selectedSection, {
            onRetryProgress: ({ message, status }) => {
                if (message && status !== 'completed') {
                    setConnectionStatus(message);
                }
            },
        })
            .then(async (data) => {
                setPacks(data);
                setConnectionStatus('');
                const viewedIds = (data || [])
                    .filter((pack) => Array.isArray(pack.files) && pack.files.length > 0)
                    .map((pack) => pack.id);
                if (viewedIds.length > 0) {
                    await markPacksViewedUser(user_id, viewedIds);
                    await fetchUnreadSummary();
                    refreshAccountBadges();
                }
            })
            .catch(error => {
                console.error('Error fetching packs:', error);
                setConnectionStatus(error.userMessage || connectionFailedMessage());
            })
            .finally(() => {
                setPacksLoading(false);
            });
    };

    const fetchUnreadSummary = async () => {
        try {
            const summary = await getPacksUnreadSummary(user_id);
            setUnreadByType(summary?.by_type || { invoices: 0, bank: 0, tax: 0, salary: 0, buh_bill: 0 });
            setSkipBankBadge(Boolean(summary?.skip_bank_badge));
        } catch (error) {
            console.error('Error fetching packs unread summary:', error);
        }
    };

    const handleFileSelect = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const currentCount = selectedFiles.length;
            const remainingSlots = Math.max(0, 5 - currentCount);
            if (remainingSlots > 0) {
                const filesToAdd = Array.from(files).slice(0, remainingSlots);
                setSelectedFiles(prevFiles => [...prevFiles, ...filesToAdd]);
            }
        }
    };

    const handleBankStatementFileInputChange = (e) => {
        const period = bankUploadPeriodRef.current;
        const files = e.target.files;

        if (!files?.length || !period) {
            e.target.value = '';
            return;
        }
        if (bankUploadBusyRef.current) {
            e.target.value = '';
            return;
        }
        if (!user_id) {
            e.target.value = '';
            return;
        }
        if (!isBankStatementPeriodClosed(period)) {
            e.target.value = '';
            return;
        }

        // Сначала копия файлов: после e.target.value = '' живой FileList часто становится пустым
        const filesArr = Array.from(files).slice(0, 5);
        e.target.value = '';
        bankUploadPeriodRef.current = null;

        bankUploadBusyRef.current = true;
        setUploadingBankPeriod(period);
        createPackFiles(user_id, false, '', filesArr, 'bank', period, (data) => {
            bankUploadBusyRef.current = false;
            setUploadingBankPeriod(null);
            if (data?.success === true) {
                fetchPacks();
                fetchUnreadSummary();
                refreshBankEmptyPeriodsCount();
            } else {
                console.error('createPackFiles (bank statement) failed', data);
            }
        });
    };

    const toggleModal = () => {
        setModalVisible(!isModalVisible);
        setComment('');
        setIsOwner(false);
        setSelectedFiles([]);
    };

    const handleCreatePack = () => {
        if (!selectedSection) return;
        const afterCreate = () => {
            setComment('');
            toggleModal();
            fetchPacks();
            fetchUnreadSummary();
        };
        if (selectedFiles.length > 0) {
            createPackFiles(user_id, isOwner, comment, selectedFiles, selectedSection, null, afterCreate);
        } else {
            createPackFiles(user_id, isOwner, comment, [], selectedSection, null, afterCreate);
        }
    };

    const requestBuhBillPaidToggle = (pack, checked) => {
        const current = normalizeBuhPaymentStatus(pack.payment_status);
        if (current === 'confirmed') return;
        setBuhPaymentConfirm({ pack, checked });
    };

    const handleBuhBillPaidToggle = async (pack, checked) => {
        if (!user_id || !pack?.id) return;
        const current = normalizeBuhPaymentStatus(pack.payment_status);
        if (current === 'confirmed') return;

        setBuhPaymentUpdatingId(pack.id);
        try {
            const result = await setBuhBillPaymentClient(user_id, pack.id, checked);
            const nextStatus = normalizeBuhPaymentStatus(result?.payment_status);
            setPacks((prev) =>
                prev.map((p) => (p.id === pack.id ? { ...p, payment_status: nextStatus } : p))
            );
            await fetchUnreadSummary();
            refreshAccountBadges();
        } catch (error) {
            console.error('buh bill payment toggle:', error);
            window.alert(error.message || 'Не удалось сохранить отметку об оплате');
        } finally {
            setBuhPaymentUpdatingId(null);
        }
    };

    const handleShowPackFiles = (files) => {
        setCurrentPackFiles(files || []);
        setPackFilesModalVisible(true);
    };

    const openSection = (sectionId) => {
        setSelectedSection(sectionId);
        setPacks([]);
    };

    const bankStatementCards = selectedSection === 'bank' ? getBankStatementCards(packs) : [];
    const q = docSearchQuery.trim().toLowerCase();

    const sectionCards = useMemo(() => {
        return PACK_SECTIONS.filter((s) => true);
    }, []);

    const filteredSectionCards = useMemo(() => {
        if (!isSearchMode || !q || selectedSection) return sectionCards;
        return sectionCards.filter((s) => s.title.toLowerCase().includes(q));
    }, [isSearchMode, q, selectedSection, sectionCards]);

    const filteredPacks = useMemo(() => {
        if (!selectedSection || selectedSection === 'bank') return packs;
        if (!isSearchMode || !q) return packs;
        return packs.filter((item) => {
            const parts = [String(item.comment ?? '')];
            if (selectedSection === 'buh_bill' && item.amount != null) {
                parts.push(String(item.amount));
            }
            (item.files || []).forEach((f) => {
                const raw = f?.path || f?.name || f?.filename || '';
                if (raw) parts.push(String(raw));
            });
            return parts.join(' ').toLowerCase().includes(q);
        });
    }, [packs, selectedSection, isSearchMode, q]);

    const filteredBankStatementCards = useMemo(() => {
        if (selectedSection !== 'bank') return bankStatementCards;
        if (!isSearchMode || !q) return bankStatementCards;
        return bankStatementCards.filter((card) => {
            const filesText = (card.files || []).map((f) => String(f?.path || '')).join(' ');
            const text = `${formatStatementPeriodLabel(card.period)} ${card.period || ''} ${filesText}`.toLowerCase();
            return text.includes(q);
        });
    }, [selectedSection, bankStatementCards, isSearchMode, q]);

    useEffect(() => {
        if (!isSearchMode || !q) {
            setSearchMatchIndices([]);
            setActiveSearchMatchIdx(0);
            return;
        }
        let matches = [];
        if (!selectedSection) {
            matches = filteredSectionCards.map((_, idx) => idx);
        } else if (selectedSection === 'bank') {
            matches = filteredBankStatementCards.map((_, idx) => idx);
        } else {
            matches = filteredPacks.map((_, idx) => idx);
        }
        setSearchMatchIndices(matches);
        setActiveSearchMatchIdx(0);
    }, [isSearchMode, q, selectedSection, filteredSectionCards, filteredBankStatementCards, filteredPacks]);

    const scrollToSearchMatch = (matchArrayIndex) => {
        const itemIndex = searchMatchIndices[matchArrayIndex];
        if (typeof itemIndex !== 'number') return;
        const targetId = !selectedSection
            ? `doc-section-${itemIndex}`
            : selectedSection === 'bank'
                ? `doc-bank-card-${itemIndex}`
                : `doc-pack-${itemIndex}`;
        const el = document.getElementById(targetId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    useEffect(() => {
        if (!isSearchMode || searchMatchIndices.length === 0) return;
        scrollToSearchMatch(activeSearchMatchIdx);
    }, [activeSearchMatchIdx, isSearchMode, searchMatchIndices]);

    const jumpSearchMatch = (direction) => {
        if (!searchMatchIndices.length) return;
        setActiveSearchMatchIdx((prev) => {
            if (direction === 'prev') return prev <= 0 ? searchMatchIndices.length - 1 : prev - 1;
            return prev >= searchMatchIndices.length - 1 ? 0 : prev + 1;
        });
    };

    const enterSearchMode = () => setIsSearchMode(true);
    const exitSearchMode = () => {
        setIsSearchMode(false);
        setDocSearchQuery('');
        setSearchMatchIndices([]);
        setActiveSearchMatchIdx(0);
    };

    useEffect(() => {
        if (docContextMenu) {
            const closeMenu = () => setDocContextMenu(null);
            document.addEventListener('click', closeMenu);
            return () => document.removeEventListener('click', closeMenu);
        }
    }, [docContextMenu]);

    const currentSectionTitle =
        PACK_SECTIONS.find((s) => s.id === selectedSection)?.title || 'Документы';

    const handleAccountSelect = (user) => {
        selectAccount(user);
        setSelectedSection(null);
    };

    const handleBackToCompanies = () => {
        openPicker();
        setSelectedSection(null);
    };

    return (
        <div className={`tasks_cntr section-page documents-page${pickerOpen ? " section-page--picker-open" : ""}`}>
            <div className="section-page-heading manrope-bold color_white">Документы</div>
            {!pickerOpen && (
            <div className="page_title_cntr section-page_title_cntr">
                {multiple && (
                    <button
                        type="button"
                        className="page-header-nav-back"
                        onClick={handleBackToCompanies}
                        aria-label="К списку компаний"
                    >
                        {arrowLIcon}
                    </button>
                )}
                <div className="manrope-bold color_white section-company-title">{username}</div>
                <div
                    className="manrope-bold color_white page_title2 section-page_actions"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    {isSearchMode ? (
                        <>
                            <input
                                value={docSearchQuery}
                                onChange={(e) => setDocSearchQuery(e.target.value)}
                                placeholder='Поиск в документах'
                                autoFocus
                                style={{
                                    height: '30px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    outline: 'none',
                                    padding: '0 10px',
                                    minWidth: '180px',
                                    maxWidth: '28vw',
                                    fontFamily: 'Manrope-Bold, sans-serif',
                                    fontSize: '13px',
                                }}
                            />
                            <button
                                type='button'
                                onClick={() => jumpSearchMatch('prev')}
                                disabled={searchMatchIndices.length === 0}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(255,255,255,0.45)',
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    cursor: searchMatchIndices.length ? 'pointer' : 'not-allowed',
                                    opacity: searchMatchIndices.length ? 1 : 0.45,
                                    padding: 0,
                                }}
                                aria-label='Предыдущее совпадение'
                            >
                                &#9650;
                            </button>
                            <button
                                type='button'
                                onClick={() => jumpSearchMatch('next')}
                                disabled={searchMatchIndices.length === 0}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(255,255,255,0.45)',
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    cursor: searchMatchIndices.length ? 'pointer' : 'not-allowed',
                                    opacity: searchMatchIndices.length ? 1 : 0.45,
                                    padding: 0,
                                }}
                                aria-label='Следующее совпадение'
                            >
                                &#9660;
                            </button>
                            <button
                                type='button'
                                onClick={exitSearchMode}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(255,255,255,0.45)',
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    padding: 0,
                                }}
                                aria-label='Закрыть поиск'
                            >
                                &#10005;
                            </button>
                        </>
                    ) : (
                        <button
                            type='button'
                            onClick={enterSearchMode}
                            style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '14px',
                                border: '1px solid rgba(255,255,255,0.45)',
                                background: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                            aria-label='Поиск'
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M11 4a7 7 0 1 0 4.4 12.44l3.58 3.58a1 1 0 0 0 1.42-1.42l-3.58-3.58A7 7 0 0 0 11 4Zm0 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10Z" fill="#FFFFFF" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
            )}
            <div className={`tasks_subcntr${pickerOpen ? " section-page-picker" : ""}`}>
                {pickerOpen ? (
                    <CompanyPickerList
                        users={users}
                        onSelect={handleAccountSelect}
                        badgeByUserId={pickerBadgeByUserId}
                    />
                ) : (
                <>
                {!selectedSection && (
                <div className="cntr_tasks documents-sections-panel">
                    <div className="documents-sections">
                            <div className="documents-group-title manrope-bold">Документы от компании</div>
                            {filteredSectionCards.filter(s => s.group === 'company').map((section, idxCompany) => {
                                const unread = Number(unreadByType[section.id]) || 0;
                                const bankBadge =
                                    section.id === 'bank'
                                        ? (skipBankBadge ? 0 : unread + bankEmptyPeriodsCount)
                                        : unread;
                                const showBadge = bankBadge > 0;
                                const badgeText = bankBadge > 99 ? '99+' : String(bankBadge);
                                return (
                                <button id={`doc-section-${filteredSectionCards.findIndex((s) => s.id === section.id)}`} key={section.id} className="documents-section-card" onClick={() => openSection(section.id)} onContextMenu={(e) => { e.preventDefault(); setDocContextMenu({ x: e.clientX, y: e.clientY, text: section.title }); }}>
                                    <div className="documents-section-card-row">
                                        <span className="documents-section-title manrope-bold">{section.title}</span>
                                        <span className="documents-section-chevron">
                                            <ChevronRightIcon />
                                        </span>
                                    </div>
                                    {showBadge && (
                                        <span className="documents-section-badge">
                                            {badgeText}
                                        </span>
                                    )}
                                </button>
                                );
                            })}
                            <div className="documents-group-title manrope-bold">Документы от бухгалтерии</div>
                            {filteredSectionCards.filter(s => s.group === 'accounting').map((section) => (
                                <button id={`doc-section-${filteredSectionCards.findIndex((s) => s.id === section.id)}`} key={section.id} className="documents-section-card" onClick={() => openSection(section.id)} onContextMenu={(e) => { e.preventDefault(); setDocContextMenu({ x: e.clientX, y: e.clientY, text: section.title }); }}>
                                    <div className="documents-section-card-row">
                                        <span className="documents-section-title manrope-bold">{section.title}</span>
                                        <span className="documents-section-chevron">
                                            <ChevronRightIcon />
                                        </span>
                                    </div>
                                    {(Number(unreadByType[section.id]) || 0) > 0 && (
                                        <span className="documents-section-badge">
                                            {Number(unreadByType[section.id]) > 99 ? '99+' : Number(unreadByType[section.id])}
                                        </span>
                                    )}
                                </button>
                            ))}
                    </div>
                </div>
                )}

                {selectedSection && (
                    <div className="documents-subsection">
                        <div className="documents-nav-sticky">
                            <div className="documents-nav-row">
                                <button
                                    type="button"
                                    className="documents-nav-back"
                                    onClick={() => setSelectedSection(null)}
                                    aria-label="Назад к разделам"
                                >
                                    <ChevronLeftIcon />
                                </button>
                                <div className="documents-nav-title manrope-bold">{currentSectionTitle}</div>
                            </div>
                        </div>
                        {(packsLoading || connectionStatus) && (
                            <div
                                style={{
                                    margin: '12px 16px',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    backgroundColor: connectionStatus ? '#fff3cd' : '#eef3ff',
                                    color: connectionStatus ? '#664d03' : '#3262FF',
                                    fontSize: '13px',
                                    lineHeight: 1.4,
                                }}
                                className="manrope-bold"
                            >
                                {connectionStatus || 'Загрузка документов…'}
                            </div>
                        )}
                        <div className="cntr_tasks documents-packs-scroll" ref={scrollContainerRef}>
                    {selectedSection && selectedSection !== 'bank' && filteredPacks.map((item, index) => {
                        const isAccountantPack = isAccountantPackSection(selectedSection);
                        const { label: dateLabel, value: dateStr } = getClientPackDateLabelAndValue(
                            item,
                            isAccountantPack
                        );
                        const amountStr = selectedSection === 'buh_bill' ? formatAmountRub(item.amount) : null;
                        const paymentStatus = selectedSection === 'buh_bill'
                            ? normalizeBuhPaymentStatus(item.payment_status)
                            : null;
                        const paymentChecked = paymentStatus ? isBuhBillPaidChecked(paymentStatus) : false;
                        const paymentConfirmed = paymentStatus === 'confirmed';
                        const isBuhBillPack = selectedSection === 'buh_bill';
                        const packTitle = isBuhBillPack
                            ? getBuhBillPackTitle(item)
                            : (item.comment || 'Без комментария');
                        return (
                        <div id={`doc-pack-${index}`} key={item.id} ref={index === filteredPacks.length - 1 ? lastPackRef : null} style={{ marginTop: '25px' }} onContextMenu={(e) => { e.preventDefault(); setDocContextMenu({ x: e.clientX, y: e.clientY, text: `${item.comment || ''}\n${amountStr || ''}\n${dateStr || ''}`.trim() }); }}>
                            <div className="task">
                                <div className="taskDetails" style={{ position: 'relative' }}>
                                    <label className="manrope-bold fs-16">{packTitle}</label>
                                </div>
                                {isBuhBillPack ? (
                                    <>
                                        <div className="tasks_line"></div>
                                        {amountStr && (
                                            <div className="taskDetails">
                                                <label className="manrope-bold color_grey fs-14">Сумма</label>
                                                <label className="manrope-bold fs-16 color_blue">{amountStr}</label>
                                            </div>
                                        )}
                                        <div className="taskDetails">
                                            <label className="manrope-bold color_grey fs-14">{dateLabel}</label>
                                            <label className="manrope-bold fs-16">{dateStr}</label>
                                        </div>
                                        {item.files && item.files.length > 0 && (
                                            <div style={{ width: '100%' }}>
                                                {item.files.map((file, fileIndex) => (
                                                    <div key={fileIndex} style={{ marginBottom: '10px' }}>
                                                        <FileMessage
                                                            path={file.path}
                                                            size={file.size}
                                                            fileAction="choice"
                                                            qrPath={file.qr_path}
                                                            compactQr
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="documents-buh-paid-row">
                                            <div className="documents-buh-checkbox-row manrope-bold fs-14">
                                                <button
                                                    type="button"
                                                    className="documents-buh-checkbox-btn"
                                                    disabled={paymentConfirmed || buhPaymentUpdatingId === item.id}
                                                    onClick={() => requestBuhBillPaidToggle(item, !paymentChecked)}
                                                    aria-label={
                                                        paymentConfirmed
                                                            ? 'Оплата подтверждена бухгалтером'
                                                            : 'После оплаты поставьте галочку'
                                                    }
                                                >
                                                    <span
                                                        className={`documents-buh-checkbox-box${paymentChecked ? ' checked' : ''}${paymentConfirmed ? ' confirmed' : ''}`}
                                                        aria-hidden
                                                    >
                                                        {paymentConfirmed ? (
                                                            <svg width="16" height="16" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path
                                                                    d="M23.28125 7.28125 L11.5 19.0625 L8.71875 16.28125 L7.28125 17.71875 L10.0625 20.5 L8 22.5625 L1.71875 16.28125 L0.28125 17.71875 L7.28125 24.71875 L8 25.40625 L8.71875 24.71875 L11.5 21.9375 L14.28125 24.71875 L15 25.40625 L15.71875 24.71875 L31.625 8.71875 L30.1875 7.28125 L15 22.5625 L12.9375 20.5 L24.71875 8.71875 Z"
                                                                    fill="#fff"
                                                                />
                                                            </svg>
                                                        ) : paymentChecked ? (
                                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M2.5 7.2L5.8 10.5L11.5 4.2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        ) : null}
                                                    </span>
                                                </button>
                                                <span className="documents-buh-checkbox-text">
                                                    {paymentConfirmed
                                                        ? 'Оплата подтверждена бухгалтером'
                                                        : 'После оплаты поставьте галочку'}
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                {amountStr && (
                                    <>
                                        <div className="tasks_line"></div>
                                        <div className="taskDetails">
                                            <label className="manrope-bold color_grey fs-14">Сумма</label>
                                            <label className="manrope-bold fs-16 color_blue">{amountStr}</label>
                                        </div>
                                    </>
                                )}
                                <div className="tasks_line"></div>
                                <div className="taskDetails">
                                    <label className="manrope-bold color_grey fs-14">{dateLabel}</label>
                                    <label className="manrope-bold fs-16">{dateStr}</label>
                                </div>
                                {item.files && item.files.length > 0 && (
                                    <div style={{ width: '100%' }}>
                                        {item.files.map((file, fileIndex) => (
                                            <div key={fileIndex} style={{ marginBottom: '10px' }}>
                                                <FileMessage
                                                    path={file.path}
                                                    size={file.size}
                                                    fileAction="choice"
                                                    qrPath={null}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                                    </>
                                )}
                            </div>
                        </div>
                        );
                    })}

                    {selectedSection === 'bank' && (
                        <>
                            <input
                                ref={bankStatementFileInputRef}
                                type="file"
                                multiple
                                style={{ display: 'none' }}
                                onChange={handleBankStatementFileInputChange}
                            />
                            {filteredBankStatementCards.map((card, index) => {
                                const firstUploadAt = getFirstFileUploadDateForCard(card);
                                const periodClosed = isBankStatementPeriodClosed(card.period);
                                const showReminderBadge = periodClosed && card.files.length === 0;
                                return (
                        <div id={`doc-bank-card-${index}`} key={card.period} style={{ marginTop: '25px' }} onContextMenu={(e) => { e.preventDefault(); setDocContextMenu({ x: e.clientX, y: e.clientY, text: `${formatStatementPeriodLabel(card.period)}\n${card.period || ''}`.trim() }); }}>
                            <div className="task">
                                <div className="taskDetails" style={{ justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                                    <label className="manrope-bold fs-16">{formatStatementPeriodLabel(card.period)}</label>
                                    <span className="documents-upload-btn-wrap">
                                        <button
                                            type="button"
                                            className="documents-upload-btn manrope-bold"
                                            disabled={!periodClosed || uploadingBankPeriod === card.period}
                                            onClick={() => {
                                                bankUploadPeriodRef.current = card.period;
                                                bankStatementFileInputRef.current?.click();
                                            }}
                                        >
                                            {uploadingBankPeriod === card.period ? '…' : 'Загрузить'}
                                        </button>
                                        {showReminderBadge && (
                                            <span className="documents-upload-reminder-badge">1</span>
                                        )}
                                    </span>
                                </div>
                                <div className="tasks_line"></div>
                                {firstUploadAt && (
                                    <div className="taskDetails" style={{ marginTop: '8px' }}>
                                        <label className="manrope-bold color_grey fs-14">Дата отправки</label>
                                        <label className="manrope-bold fs-16">
                                            {firstUploadAt.toLocaleString('ru-RU', PACK_DATETIME_OPTS)}
                                        </label>
                                    </div>
                                )}
                                {card.files.length > 0 ? (
                                    <div style={{ width: '100%', marginTop: firstUploadAt ? '8px' : 0 }}>
                                        {card.files.map((file, idx) => (
                                            <div key={`${card.period}-${idx}`} style={{ marginBottom: '10px' }}>
                                                <FileMessage path={file.path} size={file.size} fileAction="choice" />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="manrope-400 color_grey fs-14 documents-bank-empty-files" style={{ marginTop: '8px' }}>Файлы пока не загружены</div>
                                )}
                            </div>
                        </div>
                                );
                            })}
                        </>
                    )}
                        </div>
                    </div>
                )}
                </>
                )}
                {docContextMenu && (
                    <div
                        style={{
                            position: 'fixed',
                            left: docContextMenu.x,
                            top: docContextMenu.y,
                            zIndex: 10000,
                            backgroundColor: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                            padding: '4px 0',
                            minWidth: '160px',
                        }}
                    >
                        <div
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: '14px' }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                            onClick={async () => {
                                if (docContextMenu.text) {
                                    try {
                                        await navigator.clipboard.writeText(docContextMenu.text);
                                    } catch (err) {
                                        console.warn('clipboard write failed', err);
                                    }
                                }
                                setDocContextMenu(null);
                            }}
                        >
                            Копировать
                        </div>
                    </div>
                )}
            </div>

            {selectedSection === 'invoices' && !pickerOpen && (
                <div className="add_icon" onClick={toggleModal}>
                    {newTask}
                </div>
            )}

            {isModalVisible && (
                <div className="modal-container">
                    <div className="modal-content">
                        <div className="close-button" onClick={toggleModal}>
                            {closeIconTask}
                        </div>
                        <div style={{ textAlign: 'center' }} className="manrope-bold color_blue_1 fs-20">
                            Новый пакет документов
                        </div>

                        <div style={{ width: '100%', marginTop: '25px' }}>
                            <div className="manrope-bold" style={{ fontSize: '16px', textAlign: 'left' }}>Комментарий</div>
                        </div>
                        <textarea
                            className="input_task manrope-bold"
                            placeholder="Комментарий..."
                            onChange={(e) => setComment(e.target.value)}
                            value={comment}
                            rows={3}
                            style={{ resize: 'vertical', minHeight: '48px' }}
                        />

                        <div style={{ width: '100%', marginTop: '5px', marginBottom: '10px', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <span className="manrope-bold" style={{ fontSize: '14px' }}>Добавить файл</span>
                            <label htmlFor="fileInput" style={{ cursor: 'pointer', marginLeft: '10px' }}>
                                {fileSelect}
                            </label>
                            <input
                                id="fileInput"
                                type="file"
                                multiple
                                style={{ display: 'none' }}
                                onChange={handleFileSelect}
                            />
                        </div>

                        {selectedFiles.length > 0 && (
                            <div style={{ marginTop: '10px', width: '100%' }}>
                                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginBottom: '10px' }}>
                                    {selectedFiles
                                        .filter(file => file.name.match(/\.(jpg|jpeg|png|gif)$/i))
                                        .map((file, index) => (
                                            <img
                                                key={index}
                                                src={URL.createObjectURL(file)}
                                                alt="Preview"
                                                style={{ width: '40px', height: '40px', borderRadius: '7px', marginRight: '8px', marginBottom: '8px' }}
                                            />
                                        ))}
                                </div>
                                {selectedFiles
                                    .filter(file => !file.name.match(/\.(jpg|jpeg|png|gif)$/i))
                                    .map((file, index) => (
                                        <div key={index} style={{ marginBottom: '5px' }}>
                                            <span style={{ fontSize: '14px', color: '#282932' }}>
                                                {file.name || 'Документ'}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        )}

                        {selectedFiles.length >= 5 && (
                            <div style={{ fontSize: '12px', color: '#FF6B6B', marginTop: '5px' }}>
                                Достигнут лимит в 5 файлов
                            </div>
                        )}

                        <button className="create-button manrope-bold" onClick={handleCreatePack}>
                            Создать
                        </button>
                    </div>
                </div>
            )}

            {packFilesModalVisible && (
                <div className="modal-container">
                    <div className="modal-content">
                        <div className="close-button" onClick={() => setPackFilesModalVisible(false)}>
                            {closeIconTask}
                        </div>
                        <div style={{ textAlign: 'center' }} className="manrope-bold color_blue_1 fs-20">
                            Файлы пака
                        </div>
                        <div style={{ width: '100%' }}>
                            {currentPackFiles.map((file, index) => (
                                <div key={index} style={{ marginBottom: '15px' }}>
                                    <FileMessage path={file.path} size={file.size} fileAction="choice" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {buhPaymentConfirm && createPortal(
                <div
                    className="file-action-overlay"
                    role="presentation"
                    onClick={() => setBuhPaymentConfirm(null)}
                >
                    <div
                        className="file-action-card documents-buh-confirm-card"
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="file-action-title manrope-bold" style={{ textAlign: 'left' }}>
                            {buhPaymentConfirm.checked
                                ? 'Вы подтверждаете, что счет был оплачен?'
                                : 'Вы отменяете подтверждение, что счет был оплачен?'}
                        </p>
                        <div className="file-action-buttons">
                            <button
                                type="button"
                                className="file-action-btn file-action-btn-open manrope-bold"
                                onClick={() => {
                                    const { pack, checked } = buhPaymentConfirm;
                                    setBuhPaymentConfirm(null);
                                    handleBuhBillPaidToggle(pack, checked);
                                }}
                            >
                                Да
                            </button>
                            <button
                                type="button"
                                className="file-action-btn file-action-btn-cancel manrope-bold"
                                onClick={() => setBuhPaymentConfirm(null)}
                            >
                                Нет
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Documents;
